import React, { useEffect, useMemo, useRef, useState } from "react";
import ErrorMessage from "../../../forms/formComponents/ErrorMessage";
import { getClubLaunchChecklist } from "../../utils/clubLaunch";

const JOIN_OPTIONS = ["Open", "Closed", "On Approval"];
const MEMBERSHIP_OPTIONS = [
  {
    value: "hosted_stripe",
    title: "Member Nest collects membership payments",
    description:
      "Members pay online through Stripe. Stripe setup and the existing transaction fee apply.",
  },
  {
    value: "hybrid",
    title: "Let members choose online or offline",
    description:
      "Online payment gives immediate access; offline payment waits for an administrator to confirm it.",
  },
  {
    value: "club_gated",
    title: "The club collects payment before access",
    description:
      "You collect membership fees outside Member Nest and decide when each applicant may enter.",
  },
  {
    value: "club_pay_later",
    title: "Let members in and resolve payment later",
    description:
      "The normal join and approval flow grants access, while your club handles payment separately.",
  },
];

export default function ClubLaunchModal({
  isOpen,
  teamName,
  checklist,
  permissionKeys,
  availability,
  membership,
  onClose,
  onOpenChecklistItem,
  onOpenMembership,
  onManageBilling,
  onChooseMembershipMode,
  canChooseMembershipMode = false,
  onLaunch,
}) {
  const [isPublic, setIsPublic] = useState(Boolean(availability?.public));
  const [joinable, setJoinable] = useState(
    JOIN_OPTIONS.includes(availability?.joinable)
      ? availability.joinable
      : "Closed",
  );
  const [submitting, setSubmitting] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [error, setError] = useState("");
  const [launched, setLaunched] = useState(false);
  const membershipMode = membership.membershipMode || "hosted_stripe";
  const [selectedMembershipMode, setSelectedMembershipMode] =
    useState(membershipMode);
  const wasOpenRef = useRef(false);
  const launchItems = useMemo(
    () =>
      getClubLaunchChecklist(checklist, permissionKeys, {
        membershipMode,
        billingCardReady: membership.billingCardReady,
        externalPaymentReady: membership.externalPaymentReady,
      }),
    [checklist, membership.billingCardReady, membership.externalPaymentReady, membershipMode, permissionKeys],
  );
  const incompleteItems = launchItems.filter((item) => !item.completed);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setIsPublic(Boolean(availability?.public));
      setJoinable(
        JOIN_OPTIONS.includes(availability?.joinable)
          ? availability.joinable
          : "Closed",
      );
      setSubmitting(false);
      setBillingLoading(false);
      setError("");
      setLaunched(false);
      setSelectedMembershipMode(membershipMode);
    }
    wasOpenRef.current = isOpen;
  }, [availability?.joinable, availability?.public, isOpen, membershipMode]);

  if (!isOpen) return null;

  const choosingMembership =
    membership.membershipChoiceAvailable &&
    !membership.membershipChoiceConfirmed;
  const needsClubBilling = membershipMode !== "hosted_stripe" && !membership.billingCardReady;
  const stage = choosingMembership
    ? "membership-choice"
    : incompleteItems.length > 0
      ? "setup"
      : membership.loading || !membership.paid || membership.committeeReady === false
        ? "membership"
        : "launch";

  const handleLaunch = async () => {
    setSubmitting(true);
    setError("");
    try {
      await onLaunch({ live: true, public: isPublic, joinable });
      setLaunched(true);
    } catch (launchError) {
      setError(
        launchError?.message ||
          "Your club could not be launched. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBilling = async () => {
    setBillingLoading(true);
    setError("");
    try {
      await onManageBilling();
    } catch (billingError) {
      setError(billingError?.message || "Could not open billing.");
      setBillingLoading(false);
    }
  };

  const handleMembershipChoice = async () => {
    setSubmitting(true);
    setError("");
    try {
      await onChooseMembershipMode(selectedMembershipMode);
    } catch (choiceError) {
      setError(
        choiceError?.message || "Could not save the membership payment choice.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop club-launch-backdrop"
      onClick={launched || submitting ? undefined : onClose}
    >
      <div
        className="modal-content club-launch-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="club-launch-title"
        onClick={(event) => event.stopPropagation()}
      >
        {launched ? (
          <div className="club-launch-success" role="status" aria-live="polite">
            <div className="club-launch-celebration" aria-hidden="true">
              <span>🎉</span>
              <span>✨</span>
              <span>🎊</span>
            </div>
            <div className="club-launch-success__icon" aria-hidden="true">
              ✓
            </div>
            <h2 id="club-launch-title">Congratulations!</h2>
            <p>
              <strong>{teamName || "Your club"}</strong> is now live.
            </p>
            <p className="text-muted">
              {isPublic
                ? "People can now discover your club using the joining access you selected."
                : "Your club is live for members and remains hidden from public discovery."}
            </p>
            <button
              type="button"
              className="button button-primary club-launch-primary-action"
              onClick={onClose}
            >
              View your live club
            </button>
          </div>
        ) : (
          <>
            <div className="club-launch-modal__header">
              <div>
                <p className="club-launch-eyebrow">Club launch</p>
                <h2 id="club-launch-title">
                  {stage === "launch"
                    ? "Ready to go live"
                    : "Get ready to go live"}
                </h2>
              </div>
              <button
                type="button"
                className="club-launch-close"
                aria-label="Close Go Live guide"
                onClick={onClose}
                disabled={submitting}
              >
                ×
              </button>
            </div>

            <ol className="club-launch-progress" aria-label="Launch progress">
              <li
                className={
                  stage === "membership-choice" || stage === "setup"
                    ? "current"
                    : "complete"
                }
              >
                <span>1</span> Setup
              </li>
              <li
                className={
                  stage === "membership"
                    ? "current"
                    : stage === "launch"
                      ? "complete"
                      : ""
                }
              >
                <span>2</span> Membership
              </li>
              <li className={stage === "launch" ? "current" : ""}>
                <span>3</span> Access
              </li>
            </ol>

            {error && <ErrorMessage message={error} />}

            {stage === "membership-choice" && (
              <section className="club-launch-stage">
                <h3>How will your club handle membership payments?</h3>
                <p className="text-muted">
                  Choose this before payment setup. Stripe Connect is only
                  required for the first option, or later if you publish another
                  paid feature.
                </p>
                <div className="club-launch-membership-options">
                  {MEMBERSHIP_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`club-launch-choice${selectedMembershipMode === option.value ? " selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="membership-handling"
                        value={option.value}
                        checked={selectedMembershipMode === option.value}
                        onChange={() => setSelectedMembershipMode(option.value)}
                        disabled={submitting || !canChooseMembershipMode}
                      />
                      <span>
                        <strong>{option.title}</strong>
                        <small>{option.description}</small>
                      </span>
                    </label>
                  ))}
                </div>
                {selectedMembershipMode !== "hosted_stripe" && (
                  <div className="club-launch-membership-note" role="note">
                    <strong>How Member Nest will bill your club</strong>
                    <ul>
                      <li>
                        Your first 12-month billing period starts when the club
                        goes live using club-managed membership.
                      </li>
                      <li>
                        The rate is the local-currency equivalent of £1 for each
                        {selectedMembershipMode === "hybrid" ? " offline-payment account" : " account"} that remains in the club continuously for at least 24 hours.
                      </li>
                      <li>
                        Admins, managed children and course-only members count;
                        joins lasting less than 24 hours do not.
                      </li>
                      <li>
                        We charge the saved card on the first day of the month
                        after that 12-month period ends. If the club closes
                        sooner, we close and charge the current period before
                        deletion completes.
                      </li>
                      <li>
                        Finance admins can inspect the running count and charge
                        history, and receive billing notifications.
                      </li>
                    </ul>
                    <p>
                      {selectedMembershipMode === "hybrid"
                        ? "Hybrid membership also requires a connected Stripe account for members who choose immediate online payment."
                        : "A connected club Stripe account is only needed if the club later publishes another paid feature."}
                    </p>
                  </div>
                )}
                {!canChooseMembershipMode && (
                  <p className="text-muted">
                    A full club admin must make this choice.
                  </p>
                )}
                <div className="form-actions club-launch-actions">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Not yet
                  </button>
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleMembershipChoice}
                    disabled={submitting || !canChooseMembershipMode}
                  >
                    {submitting
                      ? "Saving…"
                      : selectedMembershipMode === "hosted_stripe"
                        ? "Use hosted payments"
                        : selectedMembershipMode === "hybrid"
                          ? "Use hybrid payments"
                        : "Use club-managed payments"}
                  </button>
                </div>
              </section>
            )}

            {stage === "setup" && (
              <section className="club-launch-stage">
                <h3>Finish your club setup</h3>
                <p className="text-muted">
                  Complete these essentials before members can access the club.
                </p>
                <ul className="club-launch-checklist">
                  {launchItems.map((item) => (
                    <li
                      key={item.key}
                      className={item.completed ? "complete" : ""}
                    >
                      <span
                        className="club-launch-checklist__status"
                        aria-hidden="true"
                      >
                        {item.completed ? "✓" : "!"}
                      </span>
                      <span className="club-launch-checklist__label">
                        {item.label}
                      </span>
                      {item.completed ? (
                        <span className="club-launch-checklist__done">
                          Done
                        </span>
                      ) : item.canManage ? (
                        <button
                          type="button"
                          className="button button-secondary button--xs"
                          onClick={() => onOpenChecklistItem(item.key)}
                        >
                          Set up
                        </button>
                      ) : (
                        <span className="club-launch-checklist__permission">
                          Ask an authorised admin
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {stage === "membership" && (
              <section className="club-launch-stage">
                <h3>Your membership</h3>
                {membership.loading ? (
                  <p role="status" className="text-muted">
                    Checking membership readiness…
                  </p>
                ) : (
                  <>
                    {membership.committee?.length > 0 && (
                      <div className="club-launch-membership-note" role="status">
                        <strong>Pre-launch committee</strong>
                        <ul>
                          {membership.committee.map((member) => (
                            <li key={member.displayName}>
                              {member.displayName}: {member.preparationComplete
                                ? member.paymentReady ? member.status === 'payment_failed' ? 'payment retry needed' : 'ready' : 'payment authorisation needed'
                                : 'membership setup needed'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-muted">
                      {membership.committeeReady === false && membership.paid
                        ? "Every staff-added committee administrator must finish their membership setup before launch."
                        : needsClubBilling
                        ? "Save the club billing card required for annual Member Nest charges."
                        : membership.needsBillingAttention
                        ? "Your membership billing needs attention before the club can go live."
                        : membership.hasExistingMembership
                          ? "Review your membership setup before enabling club access."
                          : "Set up your own membership using the same self-managed flow your members will use."}
                    </p>
                    {membership.error && (
                      <ErrorMessage message={membership.error} />
                    )}
                    <div className="club-launch-stage__actions">
                      {!membership.paid && <button
                        type="button"
                        className="button button-primary"
                        onClick={() =>
                          needsClubBilling
                            ? handleBilling()
                            : onOpenMembership(membership.hasExistingMembership)
                        }
                      >
                        {needsClubBilling
                          ? "Open Club Billing"
                          : membership.hasExistingMembership
                            ? "Review My Membership"
                            : "Set Up My Membership"}
                      </button>}
                      {membership.needsBillingAttention && (
                        <button
                          type="button"
                          className="button button-secondary"
                          disabled={billingLoading}
                          onClick={handleBilling}
                        >
                          {billingLoading
                            ? "Opening Billing…"
                            : "Manage Billing"}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button-secondary"
                        disabled={membership.loading}
                        onClick={membership.refresh}
                      >
                        Check again
                      </button>
                    </div>
                  </>
                )}
              </section>
            )}

            {stage === "launch" && (
              <section className="club-launch-stage">
                <h3>Choose how people can access your club</h3>
                <label className="club-launch-choice">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(event) => setIsPublic(event.target.checked)}
                    disabled={submitting}
                  />
                  <span>
                    <strong>Show this club publicly</strong>
                    <small>
                      Allow people to discover the club outside its member area.
                    </small>
                  </span>
                </label>
                <label className="club-launch-field">
                  <span>Who can join?</span>
                  <select
                    value={joinable}
                    onChange={(event) => setJoinable(event.target.value)}
                    disabled={submitting}
                  >
                    <option value="Open">Anyone can join</option>
                    <option value="On Approval">People request approval</option>
                    <option value="Closed">Joining is closed</option>
                  </select>
                </label>
                <div className="club-launch-review">
                  <strong>When you go live</strong>
                  <p>
                    Your club member area will become active.{" "}
                    {isPublic
                      ? "It will be publicly discoverable."
                      : "It will not appear in public club discovery."}
                  </p>
                </div>
                <div className="form-actions club-launch-actions">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Not yet
                  </button>
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleLaunch}
                    disabled={submitting}
                  >
                    {submitting ? "Going Live…" : "Go Live"}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
